import {client} from "@/data/axios";
import moment from "moment";

import {ResponseError} from "@/types";
import {ShortGallery, Gallery, GalleryOption, Folder, Favorite, GalleryStats, MostDownloadedPhoto} from "@/types/GalleryType";
import {resolveError} from "@/data/_resolveError";
import {Photo} from "@/types/PhotoType";

const normalizeDate = (value: any): string | null => {
    if (value === null || value === undefined || value === "") return null;
    const m = moment(value);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

export interface GalleriesPage {
    galleries: ShortGallery[];
    pagination: {
        current_page: number;
        last_page: number;
        has_more: boolean;
        total: number;
    };
}

export const my = (
    params: {page?: number, sort?: string, search?: string},
    success = (page: GalleriesPage) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/v2/galleries/my`, {params})
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const list = (
    success = (galleries: GalleryOption[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/galleries/list`)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const deleted = (
    success = (galleries: ShortGallery[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/galleries/my/deleted`)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const details = (
    gallery: string,
    success = (gallery: Gallery) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/galleries/${gallery}`)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
};

export const update = (
    gallery: string,
    form: Gallery,
    success = (data: {status: string, data: {slug: string}}) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const payload = {
        ...form,
        date: normalizeDate((form as any).date),
        storage_to_date: normalizeDate((form as any).storage_to_date),
    };
    client.post(`/galleries/${gallery}`, payload)
        .then(({data}: any) => {
            success(data);
        }).catch((error) => resolveError(error, failed));
}

export const updateCover = (
    gallery: string,
    cover_file: File,
    success = (data: {status: string, data: {url: string}}) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const formData = new FormData();

    formData.append('cover', cover_file);

    client.post(`/galleries/${gallery}/cover`, formData)
        .then(({data}: any) => {
            success(data);
        }).catch((error) => resolveError(error, failed));
}

export const updateDesign = (
    gallery: string,
    form: Gallery,
    success = (data: {status: string, data: {status: string}}) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/galleries/${gallery}/design`, form)
        .then(({data}: any) => {
            success(data);
        }).catch((error) => resolveError(error, failed));
}

export const folders = (
    gallery: string,
    success = (folders: Folder[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/galleries/${gallery}/folders`)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
}

export const createFolder = (
    gallery: string,
    form: {title: string, is_hidden: boolean, hide_photos: boolean},

    success = (folder: Folder) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/galleries/${gallery}/folders/create`, form)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
}

export const updateFolder = (
    gallery: string,
    folder: string,
    form: Folder,

    success = (folder: Folder) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/galleries/${gallery}/folders/${folder}`, form)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
}

export const deleteFolder = (
    gallery: string,
    folder: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.delete(`/galleries/${gallery}/folders/${folder}`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const sortFolders = (
    gallery: string,
    folders: Folder[],

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const sorted = folders.map(item => item.id);

    client.post(`/galleries/${gallery}/folders/sort`, {
        sorted,
    })
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const favorites = (
    gallery: string,

    success = (folders: Favorite[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/galleries/${gallery}/favorites`)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
}

export const deleteFavorite = (
    gallery: string,
    favorite: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.delete(`/galleries/${gallery}/favorites/${favorite}`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const create = (
    form: {name: string, date: string | null, storage_period: string | null, copy_gallery_id: string | null},

    success = (gallery: string) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    const payload = {
        ...form,
        date: normalizeDate(form.date),
    };
    client.post(`/galleries/create`, payload)
        .then(({data}: any) => {
            if(data.status === "success") {
                success(data.data.gallery);
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const deleteGallery = (
    gallery: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.delete(`/galleries/${gallery}/delete`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const restoreGallery = (
    gallery: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/galleries/${gallery}/restore`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const pinGallery = (
    gallery: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/galleries/${gallery}/pin`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const unpinGallery = (
    gallery: string,

    success = () => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.post(`/galleries/${gallery}/unpin`)
        .then(({data}: any) => {
            if(data.status === "success") {
                success();
            } else {
                failed(ResponseError.Unknown, {})
            }
        }).catch((error) => resolveError(error, failed));
}

export const getStats = (
    gallerySlug: string,
    success = (stats: GalleryStats) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/galleries/${gallerySlug}/stats`)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
}

export const getDownloadedPhotos = (
    gallerySlug: string,
    success = (photos: MostDownloadedPhoto[]) => {},
    failed = (type: ResponseError, data: any) => {}
) => {
    client.get(`/galleries/${gallerySlug}/downloaded-photos`)
        .then(({data}: any) => {
            success(data.data);
        }).catch((error) => resolveError(error, failed));
}

